import React from 'react';
import { useAccountStore } from '../../components/Stores/accountStore';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { useInventoryReports } from '../../hooks/useInventoryReports';
import { IInventoryReportType } from '../../sp-types/InventoryReportType';
import { EmptyInventorySummery } from './inventory-components/EmptyInventorySummery';
import MaterialsCardsBreakdown from './inventory-components/MaterialsCardsBreakdown';
import { StatusType } from '../../sp-types/statusType';
import { UnderWritingTransactionGrid } from './inventory-components/TransactionGrid/UnderWritingTransactionGrid';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
import { useUser } from '@hooks/useUser';
import { MaterialTransaction } from './inventory-components/TransactionGrid/transactionGridUtils';
import { getMaterialsCardData } from '../Dashboard/dashboardUtils';
import { MaterialTrendButton } from '../Dashboard/MaterialTrendButton';
import { GENERAL_ID, useExposureStore } from '../../components/Stores/exposureDataStore';
import { OverlayDisplayDiv } from '../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { AddMaterialPopup } from './inventory-components/AddMaterialPopup';
import { Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import * as XLSX from 'xlsx';

export default function Inventory() {
  const { account, materialColorDictionary: colorsMap } = useAccountStore();
  const { materialsMap, materials } = useMaterialsStore();
  const { pricesMap, convertionMap, getCurrencyInUSD } = useCurrencyRates();
  const {
    currentSelectedMaterialId: materialId,
    netExposure,
    setCurrentSelectedMaterialId: setMaterialId,
  } = useExposureStore();

  const { data: user } = useUser();
  const { data: inventoryReports, refetch: refetchInv } = useInventoryReports(['completed', 'underwriting']);

  const [isShowUnderwriting, setIsShowUnderwriting] = React.useState(true);
  const [editRow, setEditRow] = React.useState<MaterialTransaction | undefined>();

  const onInvAdded = (_isUpdate?: boolean) => {
    setEditRow(undefined);
    refetchInventory();
  };

  const refetchInventory = React.useCallback(() => refetchInv(), [refetchInv]);

  const getInventoryReportsByType = (
    reports: IInventoryReportType[],
    type: StatusType,
    currMaterial: string,
  ): IInventoryReportType[] => {
    return reports.filter((item) => item.status === type && [GENERAL_ID, item.materialId].includes(currMaterial));
  };

  const isShowUnderWritingGrid = (): boolean => {
    if (getInventoryReportsByType(inventoryReports || [], 'underwriting', materialId)?.length && isShowUnderwriting)
      return true;
    return false;
  };
  const openAddPopup = React.useCallback(() => {
    setEditRow({} as MaterialTransaction);
  }, [setEditRow]);

  const handleExportToExcel = React.useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    const filename = `Inventory-${account.id || 'unknown'}-${today}.xlsx`;

    // Get all inventory reports for all materials (not filtered by current material)
    const allReports = inventoryReports?.filter((item) => item.status === 'completed') || [];

    // Map directly without using getTransactions to avoid filtering by materialId
    const exportData = allReports.map((row) => ({
      'Material Name': materialsMap[row.materialId]?.name || row.materialId,
      'Material ID': row.materialId,
      'PO / Storage': row.isPO ? `${row.poId} / ${row.poBatchId}` : 'Storage',
      Supplier: row.supplier || '-',
      Quantity: row.quantity,
      Supplied: row.suppliedQty || 0,
      Remaining: row.quantity - (row.suppliedQty || 0),
      ...(row.ratio ? { Ratio: row.ratio } : {}),
      'Due Date': row.reportingTime?.toDate().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) || '',
      'Actual Price': row.actualPrice || '-',
      Currency: row.currency || 'USD',
      Excluded: row.dontSum ? 'Excluded' : '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
    XLSX.writeFile(wb, filename);
  }, [inventoryReports, materialsMap, account.id]);

  const memoNetExposure = React.useMemo(() => netExposure, [netExposure, materialId]);
  return (
    <div className="flex flex-col flex-1 h-full p-2  gap-2">
      {isShowUnderWritingGrid() && (
        <UnderWritingTransactionGrid
          colorDictionary={colorsMap}
          materialsMap={materialsMap}
          refetchInventory={refetchInventory}
          setIsShowUnderwriting={setIsShowUnderwriting}
          underWritingInventoryReports={getInventoryReportsByType(inventoryReports || [], 'underwriting', materialId)}
        />
      )}
      <MaterialTrendButton
        buttons={getMaterialsCardData(
          materialsMap,
          colorsMap,
          convertionMap,
          account.materialsToAccount,
          memoNetExposure,
        )}
        currentMaterial={materialId}
        setCurrentMaterial={setMaterialId}
      />
      <div className="shadow-custom bg-white flex flex-col flex-1 p-6 w-full gap-2 overflow-hidden">
        {inventoryReports && isInventoryEmpty(getInventoryReportsByType(inventoryReports, 'completed', materialId)) ? (
          <EmptyInventorySummery />
        ) : (
          <>
            {/* <SummaryGraphs
              inventoryReports={getInventoryReportsByType(inventoryReports || [], 'completed')}
              colorDictionary={colors}
              materialsMap={materialsMap}
              pricesMap={pricesMap}
            /> */}
            <div className="gap-3 flex flex-col overflow-auto">
              <div className="flex justify-between items-center">
                <p className="text-red text-lg">Material - {materialsMap[materialId]?.name || 'General'}</p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleExportToExcel}
                    variant="outlined"
                    sx={{
                      color: '#16a34a',
                      borderColor: '#16a34a',
                      padding: '2px 4px',
                      '&:hover': {
                        borderColor: '#15803d',
                        backgroundColor: 'rgba(22, 163, 74, 0.04)',
                      },
                      textTransform: 'none',
                    }}
                  >
                    <span className="mr-1.5">📊</span>
                    Export to Excel
                  </Button>
                  {!!user?.isEditEnabled && (
                    <Button onClick={openAddPopup} variant="outlined" startIcon={<Add />}>
                      Add Material
                    </Button>
                  )}
                </div>
              </div>
              <div className="h-full overflow-auto">
                <MaterialsCardsBreakdown
                  inventoryReports={getInventoryReportsByType(inventoryReports || [], 'completed', materialId)}
                  refetchInventory={refetchInventory}
                  colors={colorsMap}
                  materialsMap={materialsMap}
                  pricesMap={pricesMap}
                  getCurrencyInUSD={getCurrencyInUSD}
                  isEditEnabled={!!user?.isEditEnabled}
                  onEditRow={(_id, row) => setEditRow(row)}
                  isAdmin={!!user?.isAdmin}
                />
              </div>
            </div>
          </>
        )}
      </div>
      {editRow && (
        <OverlayDisplayDiv setDisplayOverlay={() => setEditRow(undefined)} hideClose>
          <AddMaterialPopup
            materials={materials}
            cancel={() => setEditRow(undefined)}
            onInvAdded={onInvAdded}
            isUpdate={!!editRow.id}
            updateRow={editRow}
          />
        </OverlayDisplayDiv>
      )}
      {/*!!user?.isEditEnabled && (
        <div className="shadow-custom bg-white w-full flex flex-row min-h-20 align-items-center p-3 gap-4 overflow-auto">
          <div className="gap-2 flex align-items-center ">
            <img className="h-8 w-8" src="/assets/icons/flat/material_generic.svg" />
            <p className="text-lg">Add materials</p>
          </div>
          <MaterialXlsUpload />
          <p>or</p>
          <AddMaterialManually
            materials={materials}
            onAddMaterial={onAddMaterial}
            editRow={editRow}
            clear={() => setEditRow(undefined)}
          />
        </div>
      )*/}
    </div>
  );
}

const isInventoryEmpty = (inventoryReports: IInventoryReportType[] | undefined | null): boolean => {
  if (!inventoryReports) return false;
  return !inventoryReports.some((item) => item.quantity > 0);
};
